// body_composition schema + Bedrock tool (BODY_COMPOSITION_PLAN.md, D-BC-1…8).
// One record per progress capture: a set of vision-estimated (or manually entered)
// body metrics, optionally anchored to a retained selfie. Pure data — safe for
// both the API (Lambda) and the web SPA.
//
// Metrics are intentionally a flexible catalog rather than fixed payload fields:
// the vision model proposes only what it can judge, and the user edits before
// saving. The catalog (BODY_METRICS) is the single source of truth that the
// extraction prompt, validation bounds, and the UI grouping all read.

/** How a single metric value was produced (D-BC-6). */
export const BODY_METRIC_SOURCES = ["vision", "manual"] as const;
export type BodyMetricSource = (typeof BODY_METRIC_SOURCES)[number];

/** Which photo angles a capture used. Front is required; side/back optional. */
export const BODY_PHOTO_ANGLES = ["front", "side", "back"] as const;
export type BodyPhotoAngle = (typeof BODY_PHOTO_ANGLES)[number];

/** The four metric families the page groups by (§4 of the plan). */
export const BODY_METRIC_FAMILIES = ["composition", "dimensions", "posture", "visual"] as const;
export type BodyMetricFamily = (typeof BODY_METRIC_FAMILIES)[number];

/** A definition in the metric catalog: how to label, group, unit, and bound a metric. */
export interface BodyMetricDef {
  id: string;
  label: string;
  family: BodyMetricFamily;
  unit: string; // default/display unit; "" for unitless scores & ratings
  min: number; // plausibility lower bound (inclusive)
  max: number; // plausibility upper bound (inclusive)
}

/**
 * The metric catalog. Bounds are deliberately generous — they only exist to drop
 * implausible vision hallucinations (e.g. a 900% body-fat reading), not to make a
 * clinical judgement. Physical measures default to metric units; the UI may offer
 * imperial display. Categorical/qualitative observations (body-shape class, spinal
 * note, bruising/edema region) live on the payload, not here, since they aren't
 * numeric trends.
 */
export const BODY_METRICS: readonly BodyMetricDef[] = [
  // ── Body composition ──
  { id: "bodyFatPct", label: "Body fat", family: "composition", unit: "%", min: 2, max: 70 },
  { id: "leanMassPct", label: "Lean mass", family: "composition", unit: "%", min: 10, max: 98 },
  { id: "estWeight", label: "Est. weight", family: "composition", unit: "kg", min: 20, max: 360 },
  { id: "bmi", label: "BMI", family: "composition", unit: "kg/m²", min: 8, max: 80 },
  { id: "visceralFatRating", label: "Visceral fat", family: "composition", unit: "", min: 1, max: 30 },
  { id: "ffmi", label: "FFMI", family: "composition", unit: "", min: 10, max: 40 },
  // ── Circumferences / dimensions (cm) ──
  { id: "neck", label: "Neck", family: "dimensions", unit: "cm", min: 5, max: 80 },
  { id: "shoulderWidth", label: "Shoulder width", family: "dimensions", unit: "cm", min: 20, max: 80 },
  { id: "chest", label: "Chest", family: "dimensions", unit: "cm", min: 40, max: 200 },
  { id: "waist", label: "Waist", family: "dimensions", unit: "cm", min: 40, max: 200 },
  { id: "hip", label: "Hip", family: "dimensions", unit: "cm", min: 40, max: 200 },
  { id: "bicepL", label: "Bicep (L)", family: "dimensions", unit: "cm", min: 15, max: 70 },
  { id: "bicepR", label: "Bicep (R)", family: "dimensions", unit: "cm", min: 15, max: 70 },
  { id: "forearmL", label: "Forearm (L)", family: "dimensions", unit: "cm", min: 12, max: 55 },
  { id: "forearmR", label: "Forearm (R)", family: "dimensions", unit: "cm", min: 12, max: 55 },
  { id: "thighL", label: "Thigh (L)", family: "dimensions", unit: "cm", min: 25, max: 110 },
  { id: "thighR", label: "Thigh (R)", family: "dimensions", unit: "cm", min: 25, max: 110 },
  { id: "calfL", label: "Calf (L)", family: "dimensions", unit: "cm", min: 20, max: 70 },
  { id: "calfR", label: "Calf (R)", family: "dimensions", unit: "cm", min: 20, max: 70 },
  { id: "waistToHip", label: "Waist-to-hip", family: "dimensions", unit: "", min: 0.5, max: 1.5 },
  { id: "shoulderToWaist", label: "Shoulder-to-waist", family: "dimensions", unit: "", min: 0.8, max: 2.5 },
  // ── Posture & symmetry ──
  { id: "shoulderTiltDeg", label: "Shoulder tilt", family: "posture", unit: "°", min: 0, max: 45 },
  { id: "hipTiltDeg", label: "Hip tilt", family: "posture", unit: "°", min: 0, max: 45 },
  { id: "headForwardScore", label: "Head-forward", family: "posture", unit: "/10", min: 0, max: 10 },
  { id: "lateralSymmetryScore", label: "L/R symmetry", family: "posture", unit: "/10", min: 0, max: 10 },
  // ── Visual / skin (coarse, qualitative — NOT diagnostic) ──
  { id: "muscleDefinitionScore", label: "Muscle definition", family: "visual", unit: "/10", min: 0, max: 10 },
  { id: "vascularityScore", label: "Vascularity", family: "visual", unit: "/10", min: 0, max: 10 },
  { id: "edemaScore", label: "Swelling/edema", family: "visual", unit: "/10", min: 0, max: 10 },
  { id: "skinToneEvenness", label: "Skin-tone evenness", family: "visual", unit: "/10", min: 0, max: 10 },
] as const;

/** Fast lookup of a metric definition by id. */
export const BODY_METRIC_BY_ID: Record<string, BodyMetricDef> = Object.fromEntries(
  BODY_METRICS.map((m) => [m.id, m]),
);

export const BODY_METRIC_IDS = BODY_METRICS.map((m) => m.id);

/** One estimated or measured metric value (D-BC-6). */
export interface BodyMetric {
  value: number;
  unit?: string; // defaults to the catalog unit when omitted
  source: BodyMetricSource; // "vision" estimate vs. "manual" ground-truth
  confidence?: number; // 0–1, vision only
}

/** Payload for a `body_composition` event (BODY_COMPOSITION_PLAN.md §5). */
export interface BodyCompositionPayload {
  measuredAt: string; // R — ISO-8601 with offset (auto-now per D-10)
  angles: BodyPhotoAngle[]; // which photos were captured (may be empty if metrics-only)
  photoFrontS3Key?: string; // retained originals, locked-down store (D-BC-5)
  photoSideS3Key?: string;
  photoBackS3Key?: string;
  thumbS3Key?: string; // face-blurred thumbnail for gallery/list views (D-BC-5)
  heightCm?: number; // optional; enables BMI / FFMI
  bodyShapeClass?: string; // categorical (e.g. ectomorph/mesomorph/endomorph, apple/pear)
  metrics: Record<string, BodyMetric>; // keyed by BODY_METRIC ids
  observations?: Record<string, string>; // qualitative notes (spinalAlignment, edemaRegion, bruising)
  notes?: string;
  // ── AI-original snapshots (frozen at capture time, D-BC-6 extension) ──────
  // Stored once on first user edit so corrections can be compared to AI originals.
  // `metrics` / `bodyShapeClass` / `observations` are the live (editable) values;
  // these hold what the vision model originally proposed.
  aiEstimates?: Record<string, { value: number; unit?: string; confidence?: number }>;
  aiBodyShapeClass?: string;
  aiObservations?: Record<string, string>; // not user-editable; image-based
}

/**
 * Bedrock tool: record body-composition metrics. Not wired into the chat capture
 * pipeline in v1 (D-BC-3 — dedicated page only); defined here so a later batch can
 * enable conversational entry without touching the schema. The dedicated extract
 * endpoint calls the validator directly with the vision model's JSON.
 */
export const RECORD_BODY_COMPOSITION_TOOL = {
  name: "record_body_composition",
  description:
    "Record body-composition / progress metrics (estimated body fat %, lean mass %, weight, " +
    "circumferences, posture/symmetry, and coarse visual scores). Provide only the metrics you can " +
    "reasonably judge; omit the rest. These are rough lifestyle estimates, NOT diagnostic measurements.",
  input_schema: {
    type: "object",
    properties: {
      measuredAt: { type: "string", description: "ISO-8601 with UTC offset. Omit if no time was given." },
      heightCm: { type: "number", description: "Patient height in cm, if known (enables BMI/FFMI)." },
      bodyShapeClass: { type: "string", description: "e.g. ectomorph/mesomorph/endomorph, apple/pear." },
      metrics: {
        type: "array",
        description: "One entry per metric you can estimate.",
        items: {
          type: "object",
          properties: {
            id: { type: "string", enum: [...BODY_METRIC_IDS], description: "Metric id from the catalog." },
            value: { type: "number" },
            unit: { type: "string", description: "Defaults to the metric's catalog unit if omitted." },
            confidence: { type: "number", description: "0–1 confidence in this estimate." },
          },
          required: ["id", "value"],
        },
      },
      notes: { type: "string" },
    },
    required: [],
  },
} as const;
